import {
  CollapsedExpandedComponentRef,
  ComponentRef,
} from '@wix/editor-platform-sdk-types';
import { CompRef } from '@wix/document-services-types';

import { PrivateAPINamespace } from '../PrivateAPINamespace';
import { HiddenComponentOptions, Role, Selector } from './refComponents.types';
import {
  createRefComponentsError,
  RefComponentsNamespaceErrorCode,
} from './errors';
import { findComponentBySelector } from './helpers';

export class RefComponentsNamespace extends PrivateAPINamespace {
  static readonly namespace = 'refComponents';

  #getFirstRefComponent(compRef: ComponentRef): ComponentRef {
    if (this.isRefComponent(compRef)) {
      return compRef;
    }

    const refComponent =
      this.ps.ds.components.refComponents.getRefHostCompPointer(compRef);

    if (!refComponent) {
      throw createRefComponentsError(
        RefComponentsNamespaceErrorCode.UnableToFindRefComponent,
        'Unable to find refComponent for the root component',
      ).withRootComponentId(compRef.id);
    }

    return refComponent as ComponentRef;
  }

  isRefComponent(compRef: ComponentRef) {
    return (
      this.ps.ds.components.getType(compRef) ===
      'wysiwyg.viewer.components.RefComponent'
    );
  }

  findComponentBySelector(compRef: ComponentRef, selector: Selector): CompRef {
    const component = findComponentBySelector(this.ps.ds, compRef, selector);

    if (!component) {
      throw createRefComponentsError(
        RefComponentsNamespaceErrorCode.UnableToFindComponentBySelector,
      )
        .withComponentId(compRef.id)
        .withSelector(selector);
    }

    return component.componentRef;
  }

  async hideReferredComponent(
    applicationId: string,
    compRef: CompRef,
  ): Promise<void> {
    this.ps.ds.runInContext(applicationId, () => {
      return this.ps.ds.components.remove(compRef);
    });
    await this.waitForChangesApplied();
  }

  async showReferredComponent(
    applicationId: string,
    compRef: CompRef,
  ): Promise<void> {
    this.ps.ds.runInContext(applicationId, () => {
      return this.ps.ds.components.refComponents.unGhostifyComponent(compRef);
    });
    await this.waitForChangesApplied();
  }

  async hideBySelector(
    applicationId: string,
    rootComp: ComponentRef,
    selector: Selector,
  ): Promise<void> {
    const compRef = this.findComponentBySelector(rootComp, selector);
    await this.hideReferredComponent(applicationId, compRef);
  }

  async showBySelector(
    applicationId: string,
    rootComp: ComponentRef,
    selector: Selector,
  ): Promise<void> {
    const compRef = this.findComponentBySelector(rootComp, selector);
    await this.showReferredComponent(applicationId, compRef);
  }

  async getHiddenComponents(
    refComponentRef: ComponentRef,
    options?: HiddenComponentOptions,
  ): Promise<CollapsedExpandedComponentRef[]> {
    if (!this.isRefComponent(refComponentRef)) {
      throw createRefComponentsError(
        RefComponentsNamespaceErrorCode.UnableToGetHiddenNoneRefComponents,
        'Unable to get hidden none ref components for the given component',
      ).withComponentId(refComponentRef.id);
    }

    const refComponents = [refComponentRef];

    if (options?.includeNested) {
      const children =
        this.ps.ds.deprecatedOldBadPerformanceApis.components.getChildren(
          refComponentRef,
          true,
        );

      children.forEach((compRef) => {
        if (this.isRefComponent(compRef as ComponentRef)) {
          refComponents.push(compRef as ComponentRef);
        }
      });
    }

    return refComponents.reduce<CollapsedExpandedComponentRef[]>(
      (acc, compRef) => {
        const ghostsComponents =
          this.ps.ds.components.refComponents.getAllGhostRefComponentsPrimaryConnection(
            compRef,
          );

        if (ghostsComponents) {
          Object.keys(ghostsComponents).forEach((ghostId) => {
            acc.push({
              componentRef: this.ps.ds.components.get.byId(
                ghostId,
              ) as ComponentRef,
              role: ghostsComponents[ghostId]?.role,
            });
          });
        }
        return acc;
      },
      [],
    );
  }

  async getHiddenComponentRoles(
    rootComp: ComponentRef,
    options?: HiddenComponentOptions,
  ): Promise<Role[]> {
    const refComponent = this.#getFirstRefComponent(rootComp);
    const hiddenComponents = await this.getHiddenComponents(
      refComponent,
      options,
    );
    return hiddenComponents.map((hiddenComponent) => hiddenComponent.role);
  }

  async findHiddenComponentByRole(
    rootComp: ComponentRef,
    role: Role,
    options?: HiddenComponentOptions,
  ): Promise<ComponentRef | null> {
    const refComponent = this.#getFirstRefComponent(rootComp);
    const hiddenComponents = await this.getHiddenComponents(
      refComponent,
      options,
    );
    const hiddenComponent = hiddenComponents.find(
      (component) => component.role === role,
    );
    return hiddenComponent?.componentRef || null;
  }

  async isHiddenBySelector(
    rootComp: ComponentRef,
    selector: Selector,
  ): Promise<boolean> {
    const bySelectorComponent = findComponentBySelector(
      this.ps.ds,
      rootComp,
      selector,
    );
    if (!bySelectorComponent) {
      return false;
    }

    const allHiddenComponents = await this.getHiddenComponentRoles(rootComp, {
      includeNested: true,
    });
    return allHiddenComponents.some(
      (hiddenComponent) => hiddenComponent === bySelectorComponent.role,
    );
  }

  setProps(compRef: ComponentRef, props: Record<string, unknown>) {
    this.ps.ds.appStudioWidgets.props.set(compRef, props);
  }

  getProps(compRef: ComponentRef) {
    return this.ps.ds.appStudioWidgets.props.get(compRef);
  }
}
